import styled from "styled-components"
import tw from "twin.macro"

export interface SpacingProps {
  spacing?: "3xl" | "2xl" | "xl" | "lg" | "sm" | "xs"
}

/* Check this when updating styled-components (spacing on ITP page is impacted), versions after 5.1.1
 * change how the `&` selector is expanded and doesn’t account for styles dependent on props.
 * Changing to `&&` works around it here, but may have wider implications.
 *
 * https://github.com/styled-components/styled-components/issues/3265
 */
/**
 * A component to add space around elements.
 */
const Spacing = styled.div.withConfig<SpacingProps>({
  shouldForwardProp: (prop, defaultValidatorFn) => {
    return !["spacing"].includes(prop) && defaultValidatorFn(prop)
  },
})`
  clear: both;
  * + && {
    ${(props) => props.spacing === "3xl" && tw`mt-12  md:mt-20`}
    ${(props) => props.spacing === "2xl" && tw`mt-12 md:mt-16`}
    ${(props) => props.spacing === "xl" && tw`mt-12`}
    ${(props) => (!props.spacing || props.spacing === "lg" ? tw`mt-8` : null)}
    ${(props) => props.spacing === "sm" && tw`mt-4`}
    ${(props) => props.spacing === "xs" && tw`mt-2`}
    &:not(:last-child) {
      ${(props) => props.spacing === "3xl" && tw`mb-12 md:mb-20`}
      ${(props) => props.spacing === "2xl" && tw`mb-12 md:mb-16`}
      ${(props) => props.spacing === "xl" && tw`mb-12`}
      ${(props) => (!props.spacing || props.spacing === "lg" ? tw`mb-8` : null)}
      ${(props) => props.spacing === "sm" && tw`mb-4`}
      ${(props) => props.spacing === "xs" && tw`mb-2`}
    }
  }
  * + &&.c-spacer-xs {
    ${tw`mt-2`}
    &:not(:last-child) {
      ${tw`mb-2`}
    }
  }
  * + &&.c-spacer-sm {
    ${tw`mt-4`}
    &:not(:last-child) {
      ${tw`mb-4`}
    }
  }
  * + &&.c-spacer-lg {
    ${tw`mt-8`}
    &:not(:last-child) {
      ${tw`mb-8`}
    }
  }
  * + &&.c-spacer-xl {
    ${tw`mt-12`}
    &:not(:last-child) {
      ${tw`mb-12`}
    }
  }
  * + &&.c-spacer-2xl {
    ${tw`mt-12 md:mt-16`}
    &:not(:last-child) {
      ${tw`mb-12 md:mb-16`}
    }
  }
  * + &&.c-spacer-3xl {
    ${tw`mt-12 md:mt-20`}
    &:not(:last-child) {
      ${tw`mt-12 md:mb-20`}
    }
  }
  h1 + &&[class*="c-spacer"],
  h2 + &&[class*="c-spacer"],
  h3 + &&[class*="c-spacer"],
  h4 + &&[class*="c-spacer"],
  h5 + &&[class*="c-spacer"],
  h6 + &&[class*="c-spacer"] {
    ${tw`mt-4`}
  }
  h1 + &&[class*="c-spacer-lg"],
  h2 + &&[class*="c-spacer-lg"],
  h3 + &&[class*="c-spacer-lg"],
  h4 + &&[class*="c-spacer-lg"],
  h5 + &&[class*="c-spacer-lg"],
  h6 + &&[class*="c-spacer-lg"] {
    ${tw`mt-8`}
  }
  h1 + &&[class*="c-spacer-xl"],
  h2 + &&[class*="c-spacer-xl"],
  h3 + &&[class*="c-spacer-xl"],
  h4 + &&[class*="c-spacer-xl"],
  h5 + &&[class*="c-spacer-xl"],
  h6 + &&[class*="c-spacer-xl"] {
    ${tw`mt-12`}
  }
`

export default Spacing
